const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uuid(value, label) {
  if (typeof value !== 'string' || !UUID.test(value)) throw new Error(`${label} must be a UUID.`);
  return value.toLowerCase();
}

function snapshot(value, jobId) {
  const keys = ['job_id', 'org_id', 'job_version', 'cost_code_template', 'template_id', 'source', 'effective_code_ids'];
  if (!value || typeof value !== 'object' || Array.isArray(value)
      || Object.keys(value).sort().join() !== [...keys].sort().join()
      || value.job_id !== jobId || !UUID.test(value.org_id)
      || !/^[0-9a-f]{32}$/.test(value.job_version)
      || !(value.cost_code_template === null || typeof value.cost_code_template === 'string')
      || !(value.template_id === null || UUID.test(value.template_id))
      || !['direct', 'template'].includes(value.source)
      || (value.source === 'template' && (value.template_id === null || value.cost_code_template === null))
      || (value.source === 'direct' && value.template_id !== null)
      || !Array.isArray(value.effective_code_ids)) {
    throw new Error('The server did not confirm an exact job cost-code snapshot.');
  }
  const ids = value.effective_code_ids.map(id => uuid(id, 'Effective cost-code ID'));
  if (new Set(ids).size !== ids.length || JSON.stringify(ids) !== JSON.stringify([...ids].sort())) {
    throw new Error('Job cost-code snapshot IDs must be unique and sorted.');
  }
  return Object.freeze({ ...value, effective_code_ids: Object.freeze(ids) });
}

async function owner(client, expectedOwnerId) {
  const { data, error } = await client.auth.getUser();
  if (error) throw error;
  const id = uuid(data?.user?.id, 'Authenticated user');
  if (expectedOwnerId && id !== expectedOwnerId) {
    throw new Error('Account changed. Reopen the job before editing cost codes.');
  }
  return id;
}

function intent(input) {
  const jobId = uuid(input.jobId, 'Job ID');
  if (!['add', 'remove'].includes(input.action)) throw new Error('Cost-code action must be add or remove.');
  return Object.freeze({
    jobId,
    costCodeId: uuid(input.costCodeId, 'Cost-code ID'),
    action: input.action,
    operationId: uuid(input.operationId, 'Operation ID'),
    ownerId: uuid(input.ownerId, 'Operation owner'),
    expectedSnapshot: snapshot(input.expectedSnapshot, jobId),
  });
}

export async function getJobCostCodeMutationSnapshot(client, jobId) {
  const id = uuid(jobId, 'Job ID');
  const ownerId = await owner(client);
  const { data, error } = await client.rpc('t4s_get_job_cost_code_snapshot', { p_job_id: id });
  if (error) throw error;
  await owner(client, ownerId);
  return snapshot(data, id);
}

/** Retain this immutable intent for the entire UI action, including retries.
 * Pass the snapshot displayed/reviewed by the user when available. The default
 * fetch is a save-time guard, not proof that the UI displayed the current set.
 */
export async function prepareJobCostCodeMutation(client, input) {
  const jobId = uuid(input.jobId, 'Job ID');
  uuid(input.costCodeId, 'Cost-code ID');
  if (!['add', 'remove'].includes(input.action)) throw new Error('Cost-code action must be add or remove.');
  if (input.operationId && !input.expectedSnapshot) {
    throw new Error('An existing operation ID requires its original snapshot.');
  }
  const ownerId = await owner(client, input.ownerId);
  const expectedSnapshot = input.expectedSnapshot
    ? snapshot(input.expectedSnapshot, jobId)
    : await getJobCostCodeMutationSnapshot(client, jobId);
  await owner(client, ownerId);
  return intent({ ...input, jobId, ownerId, expectedSnapshot,
    operationId: input.operationId || globalThis.crypto.randomUUID() });
}

/** Minimal integration: applyJobCostCodeMutation(client, {jobId,costCodeId,action}).
 * Stronger UI integration: prepare once, then call this with the SAME intent on
 * every retry. Errors after dispatch carry mutationIntent for compatibility
 * callers. Never refetch a snapshot or mint a new ID for an uncertain outcome.
 */
export async function applyJobCostCodeMutation(client, input) {
  const mutation = input.operationId
    ? intent(input)
    : await prepareJobCostCodeMutation(client, input);
  await owner(client, mutation.ownerId);
  try {
    const { data, error } = await client.rpc('t4s_apply_job_cost_code_mutation', {
      p_operation_id: mutation.operationId,
      p_job_id: mutation.jobId,
      p_cost_code_id: mutation.costCodeId,
      p_action: mutation.action,
      p_expected_snapshot: mutation.expectedSnapshot,
    });
    if (error) throw error;
    await owner(client, mutation.ownerId);
    const after = snapshot(data?.snapshot, mutation.jobId);
    const expectedIds = mutation.action === 'add'
      ? [...new Set([...mutation.expectedSnapshot.effective_code_ids, mutation.costCodeId])].sort()
      : mutation.expectedSnapshot.effective_code_ids.filter(id => id !== mutation.costCodeId);
    if (data.operation_id !== mutation.operationId || data.action !== mutation.action
        || data.cost_code_id !== mutation.costCodeId || after.org_id !== mutation.expectedSnapshot.org_id
        || after.cost_code_template !== null || after.source !== 'direct'
        || JSON.stringify(after.effective_code_ids) !== JSON.stringify(expectedIds)
        || (mutation.action === 'add' && (!UUID.test(data.job_cost_code?.id)
          || data.job_cost_code.job_id !== mutation.jobId || data.job_cost_code.cost_code_id !== mutation.costCodeId))
        || (mutation.action === 'remove' && data.job_cost_code !== null)) {
      throw new Error('The server did not confirm the complete cost-code mutation. Retry the same intent.');
    }
    return { ...data, snapshot: after, mutationIntent: mutation };
  } catch (cause) {
    const error = new Error(cause?.message || 'Job cost-code mutation failed.', { cause });
    for (const field of ['code', 'details', 'hint']) if (cause?.[field] !== undefined) error[field] = cause[field];
    error.mutationIntent = mutation;
    throw error;
  }
}
